import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const TrainerSelection = ({ onBackToClientPortal, onSelectTrainer }) => {
  const [trainers, setTrainers] = useState([]);
  const [filteredTrainers, setFilteredTrainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    philosophy: '',
    expertise: '',
    credentials: '',
    rating: '',
    priceRange: '',
    availability: ''
  });
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [showSessionOptions, setShowSessionOptions] = useState(false);
  const [sessionOptions, setSessionOptions] = useState({
    sessionDuration: 30,
    explanation: ''  // Required description of what they want to achieve
  });
  const [selectedPetId, setSelectedPetId] = useState('');
  const [pets, setPets] = useState([]);
  const [loadingPets, setLoadingPets] = useState(false);

  const clientId = localStorage.getItem('clientId');

  useEffect(() => {
    loadTrainers();
    if (clientId) {
      loadPets();
    }
  }, [clientId]);

  const loadPets = async () => {
    try {
      setLoadingPets(true);
      const response = await apiService.getClientPets(clientId);
      if (response.Data) {
        setPets(response.Data);
      }
    } catch (err) {
      console.error('Error loading pets:', err);
    } finally {
      setLoadingPets(false);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [trainers, filters]);

  const toArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map(item => (typeof item === 'string' ? item.trim() : item))
        .filter(item => Boolean(item));
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];

      // Try JSON.parse first (handles JSON arrays with double quotes)
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map(item => (typeof item === 'string' ? item.trim() : item))
            .filter(item => Boolean(item));
        }
      } catch (err) {
        // Not valid JSON, continue to other parsing methods
      }

      // Try to parse Python-style list strings (e.g., "['item1', 'item2']")
      // Check if it looks like a Python list (starts with [ and ends with ])
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          // Replace single quotes with double quotes for JSON parsing
          const jsonString = trimmed.replace(/'/g, '"');
          const parsed = JSON.parse(jsonString);
          if (Array.isArray(parsed)) {
            return parsed
              .map(item => (typeof item === 'string' ? item.trim() : item))
              .filter(item => Boolean(item));
          }
        } catch (err) {
          // If that fails, try to extract items manually
          // Remove brackets and split by comma, handling quotes
          const inner = trimmed.slice(1, -1).trim();
          if (inner) {
            // Split by comma, but be careful with quotes
            const items = inner.split(',').map(item => {
              let cleaned = item.trim();
              // Remove surrounding quotes if present
              if ((cleaned.startsWith("'") && cleaned.endsWith("'")) || 
                  (cleaned.startsWith('"') && cleaned.endsWith('"'))) {
                cleaned = cleaned.slice(1, -1);
              }
              return cleaned.trim();
            }).filter(Boolean);
            if (items.length > 0) return items;
          }
        }
      }

      // Fall back to comma-separated handling
      return trimmed
        .split(',')
        .map(item => {
          let cleaned = item.trim();
          // Remove surrounding quotes if present
          if ((cleaned.startsWith("'") && cleaned.endsWith("'")) || 
              (cleaned.startsWith('"') && cleaned.endsWith('"'))) {
            cleaned = cleaned.slice(1, -1);
          }
          return cleaned.trim();
        })
        .filter(Boolean);
    }
    return [];
  };

  const loadTrainers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getActiveTrainers();

      if (response.Code === 200) {
        const trainerList = (response.trainers || []).map(trainer => {
          const name = `${trainer.first_name || ''} ${trainer.last_name || ''}`.trim() || trainer.first_name || trainer.last_name || 'Trainer';
          const availabilityLabel = trainer.isOnline ? 'Available now' : 'Currently offline';
          const availabilityWithZone = trainer.time_zone
            ? `${availabilityLabel} · ${trainer.time_zone}`
            : availabilityLabel;

          // Parse pricing values, handling null/undefined and ensuring they're numbers
          // Handle both string and number types from backend
          const parsePrice = (value) => {
            if (value === null || value === undefined || value === '') return 0;
            const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
            return isNaN(parsed) ? 0 : parsed;
          };

          const first10MinRate = parsePrice(trainer.first_10_min_rate);
          const perMinuteRate = parsePrice(trainer.per_minute_rate);
          
          // Parse specializations - handle Python-style list strings
          const philosophy = toArray(trainer.training_philosophy);
          const expertise = toArray(trainer.areas_of_expertise);
          const credentials = toArray(trainer.credentials);

          return {
            id: trainer.id,
            name,
            businessName: trainer.business_name || 'Independent Trainer',
            rating: Number(trainer.average_rating || 0),
            reviewCount: Number(trainer.total_reviews || 0),
            first10MinRate: first10MinRate,
            perMinuteRate: perMinuteRate,
            philosophy: philosophy,
            expertise: expertise,
            credentials: credentials,
            bio: trainer.bio || 'This trainer has not added a bio yet.',
            availability: availabilityWithZone,
            timeZone: trainer.time_zone || '',
            experienceYears: trainer.experience_years || 0,
            image: null,
            raw: trainer
          };
        });

        setTrainers(trainerList);
        setError(null);
      } else {
        setTrainers([]);
        setError(response.Message || 'Failed to load trainers');
      }
    } catch (err) {
      console.error('Error loading trainers:', err);
      setError('Failed to load trainers');
      setTrainers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...trainers];

    if (filters.search) {
      filtered = filtered.filter(trainer => 
        trainer.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        trainer.businessName.toLowerCase().includes(filters.search.toLowerCase()) ||
        trainer.bio.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.philosophy) {
      filtered = filtered.filter(trainer => 
        trainer.philosophy.includes(filters.philosophy)
      );
    }

    if (filters.expertise) {
      filtered = filtered.filter(trainer => 
        trainer.expertise.includes(filters.expertise)
      );
    }

    if (filters.credentials) {
      filtered = filtered.filter(trainer => 
        trainer.credentials.includes(filters.credentials)
      );
    }

    if (filters.rating) {
      const minRating = parseFloat(filters.rating);
      filtered = filtered.filter(trainer => trainer.rating >= minRating);
    }

    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(trainer => {
        const totalRate = trainer.first10MinRate + (trainer.perMinuteRate * 20); // 30 min session
        return totalRate >= min && totalRate <= max;
      });
    }

    setFilteredTrainers(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSelectTrainer = (trainer) => {
    setSelectedTrainer(trainer);
    setShowSessionOptions(true);
  };

  const handleRequestSession = async () => {
    if (!selectedTrainer) return;
    if (!selectedPetId) {
      alert('Please select a pet for this session');
      return;
    }
    if (!sessionOptions.explanation || sessionOptions.explanation.trim().length < 50) {
      alert('Please provide a detailed explanation (at least 50 characters) of what you want to achieve in this session');
      return;
    }
    
    try {
      const response = await apiService.createSessionRequest({
        client_id: clientId,
        trainer_id: selectedTrainer.id,
        pet_id: selectedPetId,
        explanation: sessionOptions.explanation,
        duration_minutes: sessionOptions.sessionDuration
      });

      if (response.Code === 200) {
        alert(`Session request submitted! Estimated cost: $${(response.total_price || calculateSessionCost(selectedTrainer, sessionOptions.sessionDuration)).toFixed(2)}. We will notify you once the trainer accepts and it's time to pay.`);
        setShowSessionOptions(false);
        setSelectedTrainer(null);
        setSelectedPetId('');
        setSessionOptions({
          sessionDuration: 30,
          explanation: ''
        });
        if (onBackToClientPortal) {
          onBackToClientPortal();
        }
      } else {
        alert('Failed to create session request: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error creating session request:', err);
      alert('Failed to create session request. Please try again.');
    }
  };

  const calculateSessionCost = (trainer, duration) => {
    if (!trainer) {
      return 0;
    }
    
    // Ensure we're working with numbers
    const first10Min = typeof trainer.first10MinRate === 'number' 
      ? trainer.first10MinRate 
      : parseFloat(trainer.first10MinRate) || 0;
    const perMin = typeof trainer.perMinuteRate === 'number' 
      ? trainer.perMinuteRate 
      : parseFloat(trainer.perMinuteRate) || 0;
    
    // Calculate cost: first 10 minutes at first10MinRate, then per minute after
    if (duration <= 10) {
      return first10Min;
    }
    return first10Min + ((duration - 10) * perMin);
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      color: 'white',
      padding: '20px'
    },
    header: {
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      borderRadius: '15px',
      padding: '20px',
      marginBottom: '30px',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      cursor: 'pointer'
    },
    logoIcon: {
      width: '40px',
      height: '40px',
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '1.2rem'
    },
    logoText: {
      fontSize: '1.5rem',
      fontWeight: 'bold'
    },
    backButton: {
      background: 'transparent',
      border: '2px solid #ecf0f1',
      color: '#ecf0f1',
      padding: '10px 20px',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '1rem',
      transition: 'all 0.3s ease'
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: '10px',
      color: '#f39c12'
    },
    subtitle: {
      fontSize: '1.2rem',
      textAlign: 'center',
      marginBottom: '40px',
      color: 'rgba(255, 255, 255, 0.9)'
    },
    filtersSection: {
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      borderRadius: '15px',
      padding: '30px',
      marginBottom: '30px',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
    },
    filtersTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '20px',
      color: '#f39c12'
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px'
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#f39c12'
    },
    input: {
      padding: '12px',
      borderRadius: '8px',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      fontSize: '1rem',
      transition: 'all 0.3s ease'
    },
    select: {
      padding: '12px',
      borderRadius: '8px',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      fontSize: '1rem',
      cursor: 'pointer'
    },
    trainersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '20px'
    },
    trainerCard: {
      background: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(10px)',
      borderRadius: '15px',
      padding: '25px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      color: 'white'
    },
    trainerCardHover: {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
      border: '1px solid rgba(255, 255, 255, 0.4)'
    },
    trainerHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '15px'
    },
    trainerName: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: 'white',
      margin: 0
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: '0.9rem',
      color: 'white'
    },
    businessName: {
      fontSize: '0.9rem',
      color: 'white',
      marginBottom: '10px'
    },
    bio: {
      fontSize: '0.9rem',
      color: 'white',
      lineHeight: '1.5',
      marginBottom: '15px'
    },
    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '15px'
    },
    tag: {
      background: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: '500',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      display: 'inline-block'
    },
    pricing: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '15px'
    },
    priceInfo: {
      fontSize: '0.9rem',
      color: 'white'
    },
    priceAmount: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: 'white'
    },
    availability: {
      fontSize: '0.9rem',
      color: 'white',
      fontWeight: '600',
      marginBottom: '15px'
    },
    selectButton: {
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%'
    },
    sessionModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    sessionContent: {
      background: 'white',
      borderRadius: '15px',
      padding: '40px',
      maxWidth: '500px',
      width: '100%',
      color: '#2c3e50'
    },
    sessionTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#2c3e50',
      textAlign: 'center'
    },
    sessionTrainer: {
      fontSize: '1.2rem',
      fontWeight: '600',
      marginBottom: '30px',
      color: '#f39c12',
      textAlign: 'center'
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '20px'
    },
    checkbox: {
      width: '20px',
      height: '20px',
      cursor: 'pointer'
    },
    checkboxLabel: {
      fontSize: '1rem',
      cursor: 'pointer'
    },
    warningText: {
      fontSize: '0.9rem',
      color: '#e74c3c',
      marginTop: '10px',
      fontStyle: 'italic'
    },
    durationField: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '20px'
    },
    durationLabel: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#2c3e50'
    },
    durationSelect: {
      padding: '12px',
      borderRadius: '8px',
      border: '2px solid #ddd',
      background: 'white',
      color: '#2c3e50',
      fontSize: '1rem',
      cursor: 'pointer'
    },
    notesField: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '30px'
    },
    notesLabel: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#2c3e50'
    },
    notesTextarea: {
      padding: '12px',
      borderRadius: '8px',
      border: '2px solid #ddd',
      background: 'white',
      color: '#2c3e50',
      fontSize: '1rem',
      minHeight: '80px',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    costDisplay: {
      background: 'rgba(46, 204, 113, 0.1)',
      border: '1px solid #2ecc71',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '30px',
      textAlign: 'center'
    },
    costLabel: {
      fontSize: '1rem',
      color: '#2c3e50',
      marginBottom: '5px'
    },
    costAmount: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#2ecc71'
    },
    modalButtons: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center'
    },
    startButton: {
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      border: 'none',
      padding: '15px 30px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    cancelButton: {
      background: 'transparent',
      color: '#e74c3c',
      border: '2px solid #e74c3c',
      padding: '15px 30px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    error: {
      background: 'rgba(231, 76, 60, 0.2)',
      border: '1px solid #e74c3c',
      color: '#e74c3c',
      padding: '15px',
      borderRadius: '10px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    loading: {
      textAlign: 'center',
      color: '#f39c12',
      fontSize: '1.1rem',
      marginTop: '20px'
    },
    noResults: {
      textAlign: 'center',
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: '1.2rem',
      marginTop: '40px'
    }
  };

  const philosophyOptions = [
    'Force Free / Pure Positive',
    'Positive Reinforcement Based (+R)',
    'Clicker / Marker Training',
    'Fear Free',
    'Science Based',
    'Relationship Based',
    'Balanced',
    'LIMA (Least Intrusive, Minimally Aversive)',
    'Traditional Training Methods',
    'Alpha / Pack Based',
    'Remote Training'
  ];

  const expertiseOptions = [
    'Puppy',
    'Manners',
    'Behavior Modification',
    'Aggression Management – Dog to dog',
    'Aggression Management - human directed',
    'Reactivity',
    'Separation Anxiety',
    'Anxiety / Fears / Phobias',
    'Leash Walking',
    'Potty Training',
    'Rescue Rehab',
    'Service Dog',
    'Therapy Dog',
    'Protection',
    'Tracking',
    'Competition Obedience',
    'Scentwork',
    'Agility',
    'Conformation',
    'Herding',
    'Earthdog'
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo} onClick={onBackToClientPortal}>
            <img 
              src="/images/logo.png" 
              alt="Pheefee Logo" 
              style={{...styles.logoIcon, width: '40px', height: '40px', objectFit: 'contain'}}
            />
            <span style={styles.logoText}>Pheefee</span>
          </div>
          <button
            onClick={onBackToClientPortal}
            style={styles.backButton}
          >
            ← Back to Client Portal
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        <h1 style={styles.title}>Choose Your Trainer</h1>
        <p style={styles.subtitle}>
          Select from our professional dog trainers based on your specific needs
        </p>

        {/* Filters */}
        <div style={styles.filtersSection}>
          <h3 style={styles.filtersTitle}>Filter Trainers</h3>
          <div style={styles.filtersGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={styles.input}
                placeholder="Search by name, business, or expertise..."
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Training Philosophy</label>
              <select
                value={filters.philosophy}
                onChange={(e) => handleFilterChange('philosophy', e.target.value)}
                style={styles.select}
              >
                <option value="">All Philosophies</option>
                {philosophyOptions.map(philosophy => (
                  <option key={philosophy} value={philosophy}>{philosophy}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Area of Expertise</label>
              <select
                value={filters.expertise}
                onChange={(e) => handleFilterChange('expertise', e.target.value)}
                style={styles.select}
              >
                <option value="">All Expertise Areas</option>
                {expertiseOptions.map(expertise => (
                  <option key={expertise} value={expertise}>{expertise}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Credentials</label>
              <select
                value={filters.credentials}
                onChange={(e) => handleFilterChange('credentials', e.target.value)}
                style={styles.select}
              >
                <option value="">All Credentials</option>
                <option value="CDT">CDT (Certified Dog Trainer)</option>
                <option value="CPDT">CPDT (Certified Professional Dog Trainer)</option>
                <option value="CPDT-KA">CPDT-KA (CPDT Knowledge Assessed)</option>
                <option value="KPA-CTP">KPA-CTP (Karen Pryor Academy Certified Training Partner)</option>
                <option value="CBCC-KA">CBCC-KA (Certified Behavior Consultant Canine - Knowledge Assessed)</option>
                <option value="CCPDT">CCPDT (Certification Council for Professional Dog Trainers)</option>
                <option value="IAABC">IAABC (International Association of Animal Behavior Consultants)</option>
                <option value="VSPDT">VSPDT (Victoria Stilwell Positively Dog Training)</option>
                <option value="ABC">ABC (Animal Behavior College)</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Minimum Rating</label>
              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange('rating', e.target.value)}
                style={styles.select}
              >
                <option value="">All Ratings</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="4.0">4.0+ Stars</option>
                <option value="3.5">3.5+ Stars</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Price Range (30 min session)</label>
              <select
                value={filters.priceRange}
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                style={styles.select}
              >
                <option value="">All Prices</option>
                <option value="0-30">Under $30</option>
                <option value="30-50">$30 - $50</option>
                <option value="50-80">$50 - $80</option>
                <option value="80-100">$80+</option>
              </select>
            </div>
          </div>
        </div>

        {/* Trainers Grid */}
        {error && <div style={styles.error}>{error}</div>}
        {loading && <div style={styles.loading}>Loading trainers...</div>}
        
        {!loading && !error && trainers.length === 0 && (
          <div style={styles.noResults}>
            No trainers are currently accepting new sessions. Please check back soon.
          </div>
        )}

        {!loading && !error && trainers.length > 0 && filteredTrainers.length === 0 && (
          <div style={styles.noResults}>
            No trainers found matching your criteria. Try adjusting your filters.
          </div>
        )}

        {!loading && !error && filteredTrainers.length > 0 && (
          <div style={styles.trainersGrid}>
            {filteredTrainers.map(trainer => (
              <div
                key={trainer.id}
                style={styles.trainerCard}
              >
                <div style={styles.trainerHeader}>
                  <h3 style={styles.trainerName}>{trainer.name}</h3>
                  <div style={styles.rating}>
                    ⭐ {trainer.rating} ({trainer.reviewCount} reviews)
                  </div>
                </div>
                
                <div style={styles.businessName}>{trainer.businessName}</div>
                <div style={styles.bio}>{trainer.bio}</div>
                
                {/* Training Philosophy */}
                {trainer.philosophy && trainer.philosophy.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.85rem', color: 'white', marginBottom: '6px', fontWeight: '600' }}>
                      Training Philosophy:
                    </div>
                    <div style={styles.tags}>
                      {trainer.philosophy.map(phil => (
                        <span key={phil} style={styles.tag}>{phil}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Areas of Expertise / Specializations */}
                {trainer.expertise && trainer.expertise.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.85rem', color: 'white', marginBottom: '6px', fontWeight: '600' }}>
                      Specializations:
                    </div>
                    <div style={styles.tags}>
                      {trainer.expertise.map(exp => (
                        <span key={exp} style={styles.tag}>{exp}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Credentials */}
                {trainer.credentials && trainer.credentials.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.85rem', color: 'white', marginBottom: '6px', fontWeight: '600' }}>
                      Credentials:
                    </div>
                    <div style={styles.tags}>
                      {trainer.credentials.map(cred => (
                        <span key={cred} style={styles.tag}>{cred}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div style={styles.pricing}>
                  <div style={styles.priceInfo}>
                    <div style={{ color: 'white' }}>30-min session:</div>
                    <div style={styles.priceAmount}>
                      ${calculateSessionCost(trainer, 30).toFixed(2)}
                    </div>
                  </div>
                  <div style={styles.availability}>{trainer.availability}</div>
                </div>
                
                <button
                  style={styles.selectButton}
                  onClick={() => handleSelectTrainer(trainer)}
                >
                  Select This Trainer
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Session Options Modal */}
        {showSessionOptions && selectedTrainer && (
          <div style={styles.sessionModal}>
            <div style={styles.sessionContent}>
              <h2 style={styles.sessionTitle}>Session Options</h2>
              <div style={styles.sessionTrainer}>
                Selected: {selectedTrainer.name}
              </div>
              
              <div style={styles.durationField}>
                <label style={styles.durationLabel}>Select Pet *</label>
                {loadingPets ? (
                  <div>Loading pets...</div>
                ) : pets.length === 0 ? (
                  <div style={{ color: '#e74c3c', fontSize: '0.9rem' }}>
                    No pets found. Please add a pet in your profile first.
                  </div>
                ) : (
                  <select
                    value={selectedPetId}
                    onChange={(e) => setSelectedPetId(e.target.value)}
                    style={styles.durationSelect}
                    required
                  >
                    <option value="">Select a pet...</option>
                    {pets.map(pet => (
                      <option key={pet.id} value={pet.id}>
                        {pet.pet_name} {pet.race ? `(${pet.race})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              <div style={styles.durationField}>
                <label style={styles.durationLabel}>Session Duration (minutes) *</label>
                <select
                  value={sessionOptions.sessionDuration}
                  onChange={(e) => setSessionOptions({
                    ...sessionOptions,
                    sessionDuration: parseInt(e.target.value)
                  })}
                  style={styles.durationSelect}
                >
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                </select>
              </div>
              
              <div style={styles.notesField}>
                <label style={styles.notesLabel}>
                  What are you looking to achieve in this session? * 
                  <span style={{ fontSize: '0.85rem', color: '#7f8c8d', fontWeight: 'normal' }}>
                    (Minimum 50 characters)
                  </span>
                </label>
                <textarea
                  value={sessionOptions.explanation}
                  onChange={(e) => setSessionOptions({
                    ...sessionOptions,
                    explanation: e.target.value
                  })}
                  style={styles.notesTextarea}
                  placeholder="Please provide a detailed explanation of what you want to achieve in this training session. This helps the trainer understand your goals and prepare accordingly..."
                  minLength={50}
                  required
                />
                <div style={{ fontSize: '0.85rem', color: sessionOptions.explanation.length < 50 ? '#e74c3c' : '#27ae60', marginTop: '5px' }}>
                  {sessionOptions.explanation.length}/50 characters
                </div>
              </div>
              
              <div style={styles.costDisplay}>
                <div style={styles.costLabel}>Session Cost:</div>
                <div style={styles.costAmount}>
                  ${calculateSessionCost(selectedTrainer, sessionOptions.sessionDuration).toFixed(2)}
                </div>
              </div>
              
              <div style={styles.modalButtons}>
                <button
                  style={styles.startButton}
                  onClick={handleRequestSession}
                  disabled={!selectedPetId || !sessionOptions.explanation || sessionOptions.explanation.trim().length < 50}
                >
                  Proceed to Payment
                </button>
                <button
                  style={styles.cancelButton}
                  onClick={() => setShowSessionOptions(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainerSelection;